import type { SetNonNullable } from "type-fest";

export const hasBoundsForRange = (
  range: FrameRange,
): range is SetNonNullable<typeof range, "lower" | "upper"> =>
  range.lower != null && range.upper != null;

export const spanForRange = (first: FrameRange, second: FrameRange) => ({
  lower: [first.lower, second.lower]
    .filter((bound) => bound != null)
    .reduce<
      number | null
    >((min, val) => Math.min(min ?? Number.POSITIVE_INFINITY, val), null),
  upper: [first.upper, second.upper]
    .filter((bound) => bound != null)
    .reduce<
      number | null
    >((max, val) => Math.max(max ?? Number.NEGATIVE_INFINITY, val), null),
});

/**
 * Create a new range where the bounds are assigned to the first lower and upper
 * bounds that is not `null`.
 *
 * Defaults applied from left to right.
 */
export const defaultsForRange = (
  top: FrameRange,
  bottom: FrameRange,
): FrameRange => ({
  lower: top.lower ?? bottom.lower,
  upper: top.upper ?? bottom.upper,
});

/**
 * Returns `true` if value is within the bounds of the range.
 *
 * An unbound range (one of the bounds is `null`) doesn't contain anything.
 */
export const containsForRange = (range: FrameRange, value: number) =>
  (range.lower ?? Number.POSITIVE_INFINITY) <= value &&
  value < (range.upper ?? Number.NEGATIVE_INFINITY);

/**
 * An inclusive start, exclusive range.
 *
 * For example, `1-1` empty.
 */
export interface FrameRange {
  lower: number | null;
  upper: number | null;
}
